import React, { useState, useEffect } from 'react'
import { useApp } from '../hooks/useApp'
import { useNavigate } from 'react-router-dom'
import { getJobStatusLabel, isJobFailed, isJobProcessing, isJobSuccessful } from '../utils/jobStatus'

// Helper functions
function getProgressPercentage(status) {
  const progressMap = {
    'pending': 0,
    'queued': 5,
    'starting': 10,
    'executing_parallel_streams': 20,
    'executing_step1': 30,
    'executing_step2': 50,
    'executing_step2_format': 70,
    'executing_step3': 80,
    'executing_convergence': 90,
    'completed': 100,
    'partial_success': 100,
    'partially_completed': 100,
    'failed': 0
  }
  
  return progressMap[status] || 25
}

function formatDate(dateString) {
  if (!dateString) return 'Unknown date'
  
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return 'Invalid date'
  }
}

function JobsHistory({ jobs, onFileView }) {
  const { clearAllJobs } = useApp()
  const [sortField, setSortField] = useState('date')
  const [sortOrder, setSortOrder] = useState('desc')
  const [filterStatus, setFilterStatus] = useState('all')

  const handleClearAll = async () => {
    const completedJobs = jobs.filter(job => isJobSuccessful(job.status)).length
    const totalJobs = jobs.length
    
    let message = `Delete all ${totalJobs} job${totalJobs === 1 ? '' : 's'} from your history?\n\n`
    
    if (completedJobs > 0) {
      message += `⚠️  This will permanently delete:\n`
      message += `• ${completedJobs} completed SEO analysis report${completedJobs === 1 ? '' : 's'}\n`
      message += `• All action recommendations\n`
      message += `• All executive summaries\n`
    } else {
      message += `This will remove all jobs from your history.\n\n`
    }
    
    message += `This action cannot be undone.`
    
    if (!confirm(message)) {
      return
    }
    
    try {
      await clearAllJobs()
    } catch (error) {
      alert(`Failed to clear jobs: ${error.message}`)
    }
  }

  if (!jobs || jobs.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <div className="text-gray-400 text-lg mb-2">📋</div>
        <h3 className="text-lg font-medium text-foreground mb-1">No jobs yet</h3>
        <p className="text-sm text-muted-foreground">Submit a URL above to start your first SEO analysis</p>
      </div>
    )
  }

  // Filter jobs by status
  const filteredJobs = filterStatus === 'all' 
    ? jobs 
    : jobs.filter(job => {
        if (filterStatus === 'completed') return isJobSuccessful(job.status)
        if (filterStatus === 'processing') return isJobProcessing(job.status)
        if (filterStatus === 'failed') return isJobFailed(job.status)
        return true
      })

  // Sort jobs
  const sortedJobs = [...filteredJobs].sort((a, b) => {
    if (sortField === 'date') {
      const dateA = new Date(a.created_at || a.createdAt || a.timestamp || a.date || 0)
      const dateB = new Date(b.created_at || b.createdAt || b.timestamp || b.date || 0)
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
    }
    if (sortField === 'domain') {
      const domainA = new URL(a.url).hostname.toLowerCase()
      const domainB = new URL(b.url).hostname.toLowerCase()
      return sortOrder === 'desc' ? domainB.localeCompare(domainA) : domainA.localeCompare(domainB)
    }
    if (sortField === 'status') {
      return sortOrder === 'desc' ? b.status.localeCompare(a.status) : a.status.localeCompare(b.status)
    }
    return 0
  })

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header with Filters */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Recent Jobs</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => window.location.reload()}
              className="text-sm text-muted-foreground hover:text-gray-800 px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
            >
              <svg
                className="w-4 h-4 inline mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
            <button
              onClick={handleClearAll}
              className="text-sm text-red-600 hover:text-red-700 px-3 py-1.5 rounded-md hover:bg-red-50 transition-colors"
            >
              Delete All
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setFilterStatus("all")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filterStatus === "all"
                ? "bg-gray-900 text-white"
                : "text-muted-foreground hover:text-foreground hover:bg-gray-100"
            }`}
          >
            All ({jobs.length})
          </button>
          <button
            onClick={() => setFilterStatus("completed")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filterStatus === "completed"
                ? "bg-green-600 text-white"
                : "text-muted-foreground hover:text-foreground hover:bg-gray-100"
            }`}
          >
            Completed ({jobs.filter((j) => isJobSuccessful(j.status)).length})
          </button>
          <button
            onClick={() => setFilterStatus("processing")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filterStatus === "processing"
                ? "bg-blue-600 text-white"
                : "text-muted-foreground hover:text-foreground hover:bg-gray-100"
            }`}
          >
            Processing (
            {
              jobs.filter((j) => isJobProcessing(j.status)).length
            }
            )
          </button>
          <button
            onClick={() => setFilterStatus("failed")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filterStatus === "failed"
                ? "bg-red-600 text-white"
                : "text-muted-foreground hover:text-foreground hover:bg-gray-100"
            }`}
          >
            Failed ({jobs.filter((j) => j.status === "failed").length})
          </button>
        </div>
      </div>

      {/* Compact Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-background border-b border-border">
            <tr>
              <th
                className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-card-foreground"
                onClick={() => handleSort("domain")}
              >
                <div className="flex items-center">
                  Website
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {sortField === "domain" && sortOrder === "asc" ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    )}
                  </svg>
                </div>
              </th>
              <th
                className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-card-foreground"
                onClick={() => handleSort("status")}
              >
                <div className="flex items-center">
                  Status
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {sortField === "status" && sortOrder === "asc" ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    )}
                  </svg>
                </div>
              </th>
              <th
                className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-card-foreground"
                onClick={() => handleSort("date")}
              >
                <div className="flex items-center">
                  Date
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {sortField === "date" && sortOrder === "asc" ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    )}
                  </svg>
                </div>
              </th>
              <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedJobs.map((job) => (
              <JobRow key={job.id} job={job} onFileView={onFileView} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobRow({ job, onFileView }) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const { deleteJob } = useApp()

  const handleDelete = async (e) => {
    e.stopPropagation()
    
    const formatUrl = (url) => {
      try {
        const urlObj = new URL(url)
        const host = urlObj.hostname.replace(/^www\./, '')
        const firstSegment = urlObj.pathname.split('/').filter(Boolean)[0]
        return firstSegment ? `${host}/${firstSegment}` : host
      } catch {
        return url
      }
    }

    const website = formatUrl(job.url)
    const hasResults = isJobSuccessful(job.status)
    const isInProgress = isJobProcessing(job.status)

    let message = ''

    if (isInProgress) {
      message = `Cancel and delete the in-progress analysis for ${website}?\n\n`
      message += `⚠️  This will:\n`
      message += `• Stop the current analysis\n`
      message += `• Remove the job from your history\n`
      message += `• Delete any partial results\n`
    } else if (hasResults) {
      message = `Delete analysis for ${website}?\n\n`
      message += `⚠️  This will permanently delete:\n`
      message += `• SEO audit report\n`
      message += `• Action recommendations\n`
      message += `• Executive summary\n`
    } else {
      message = `Delete analysis for ${website}?\n\n`
      message += `This will remove the job from your history.\n`
    }

    message += `\nThis action cannot be undone.`
    
    if (!confirm(message)) {
      return
    }

    setIsDeleting(true)
    try {
      const result = await deleteJob(job.id)
      if (!result.success) {
        alert(`Failed to delete job: ${result.error}`)
      }
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setIsDeleting(false)
    }
  }

  const getStatusBadge = (status) => {
    const variants = {
      'completed': 'bg-green-100 text-green-800',
      'partial_success': 'bg-green-100 text-green-800',
      'partially_completed': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      'processing': 'bg-blue-100 text-blue-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'queued': 'bg-yellow-100 text-yellow-800'
    }
    
    if (variants[status]) {
      return variants[status]
    }
    
    if (status.includes('executing') || status.includes('processing') || status.includes('step')) {
      return 'bg-blue-100 text-blue-800'
    }
    
    return 'bg-gray-100 text-gray-800'
  }

  const getStatusText = (status) => getJobStatusLabel(status)

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'partial_success':
      case 'partially_completed':
        return '✅'
      case 'failed': return '❌'
      default: return '⏳'
    }
  }

  const formatUrl = (url) => {
    try {
      const urlObj = new URL(url)
      const host = urlObj.hostname.replace(/^www\./, '')
      const firstSegment = urlObj.pathname.split('/').filter(Boolean)[0]
      return firstSegment ? `${host}/${firstSegment}` : host
    } catch {
      return url
    }
  }

  const isProcessing = isJobProcessing(job.status)

  return (
    <tr 
      className="hover:bg-background group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Website */}
      <td className="py-3 px-4">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground truncate max-w-xs" title={job.url}>
            {formatUrl(job.url)}
          </span>
          {isProcessing && showProgress && (
            <div className="mt-1">
              <div className="flex items-center text-xs text-muted-foreground mb-1">
                <span>Progress: {getProgressPercentage(job.status)}%</span>
              </div>
              <div className="w-32 bg-gray-200 rounded-full h-1">
                <div 
                  className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage(job.status)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="py-3 px-4">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{getStatusIcon(job.status)}</span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(job.status)}`}>
            {getStatusText(job.status)}
          </span>
          {isProcessing && (
            <button
              onClick={() => setShowProgress(!showProgress)}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              {showProgress ? 'Hide' : 'Details'}
            </button>
          )}
        </div>
      </td>

      {/* Date */}
      <td className="py-3 px-4 text-sm text-muted-foreground">
        {formatDate(job.created_at || job.createdAt || job.timestamp || job.date)}
      </td>

      {/* Actions */}
      <td className="py-3 px-4">
        <div className="flex items-center justify-end gap-2">
          {isJobSuccessful(job.status) ? (
            <GenerateActionsButton 
              key={`${job.id}-${job.status}-${job.action_count || 0}`}
              jobId={job.id} 
              url={job.url} 
              folderPath={job.folderPath}
              actionCount={job.action_count}
              actionGenerationStatus={job.action_generation_status}
            />
          ) : job.steps?.actions_parsing?.status === 'in_progress' || job.action_generation_status === 'generating' ? (
            <span className="text-xs text-blue-600 flex items-center">
              <span className="animate-spin mr-1">⚙️</span> Generating...
            </span>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
          
          {/* Delete button - visible for all jobs */}
          {(isProcessing || isHovered) && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="text-gray-400 hover:text-red-600 disabled:opacity-50 p-1 transition-colors"
              title={isProcessing ? "Cancel and delete job" : "Delete job"}
            >
              {/* Trash icon for all jobs */}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}


function GenerateActionsButton({ jobId, url, folderPath, actionCount, actionGenerationStatus }) {
  const navigate = useNavigate()
  const [isGenerating, setIsGenerating] = useState(false)
  const [status, setStatus] = useState(null) // null, 'success', 'error'
  const [hasActions, setHasActions] = useState(false)
  const [isChecking, setIsChecking] = useState(true)
  const [localActionCount, setLocalActionCount] = useState(0)

  // Check if actions exist for this job
  useEffect(() => {
    // If we have actionCount from props, use it
    if (actionCount !== undefined && actionCount > 0) {
      setHasActions(true)
      setLocalActionCount(actionCount)
      setIsChecking(false)
    } else if (actionGenerationStatus === 'completed' && actionCount === 0) {
      setHasActions(false)
      setIsChecking(false)
    } else {
      checkForExistingActions()
    }
  }, [jobId, actionCount, actionGenerationStatus])

  const checkForExistingActions = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return
      
      // Check if actions exist for this specific job
      const response = await fetch(`/api/v1/actions/check-job/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (response.ok) {
        const result = await response.json()
        setHasActions(result.hasActions)
        // Update actionCount if available from the check
        if (result.actionCount !== undefined && result.actionCount > 0) {
          // We can't directly update the parent's state, but we can use the count for display
          setLocalActionCount(result.actionCount)
        }
      }
    } catch (error) {
      console.error('Error checking for actions:', error)
    } finally {
      setIsChecking(false)
    }
  }

  const siteKeyFromUrl = (rawUrl) => {
    try {
      const urlObj = new URL(rawUrl)
      const host = urlObj.hostname.replace(/^www\./, '')
      const firstSegment = urlObj.pathname.split('/').filter(Boolean)[0]
      return firstSegment ? `${host}/${firstSegment}` : host
    } catch {
      return rawUrl
    }
  }

  const handleButtonClick = async () => {
    // If actions exist, navigate to dashboard
    if (hasActions) {
      const domain = siteKeyFromUrl(url)
      navigate(`/dashboard/actions?domain=${encodeURIComponent(domain)}&jobId=${jobId}`)
      return
    }
    
    // Otherwise, generate actions
    await handleGenerateActions()
  }

  const handleGenerateActions = async () => {
    if (isGenerating) return
    
    console.log('Generate Actions clicked for job:', jobId, 'url:', url, 'folderPath:', folderPath);
    
    setIsGenerating(true)
    setStatus(null)
    
    try {
      const token = localStorage.getItem('auth_token')
      console.log('Auth token exists:', !!token);
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.')
      }
      
      // If we have folderPath, use the parse endpoint
      // Otherwise, try to re-run just Step 5 for this job
      const endpoint = folderPath 
        ? '/api/v1/actions/parse'
        : `/api/v1/jobs/${jobId}/regenerate-actions`;
      
      console.log('Using endpoint:', endpoint);
      
      const body = folderPath 
        ? { folderPath } 
        : { jobId };
      
      console.log('Request body:', body);
      
      console.log('Making request to:', endpoint);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json()
        console.error('Error response:', errorData);
        
        // Special handling for old jobs not in database
        if (response.status === 404 && !folderPath) {
          throw new Error('This analysis is too old. Please run a new analysis to generate actions.')
        }
        
        throw new Error(errorData.error || `Failed to generate actions (${response.status})`)
      }

      const result = await response.json()
      console.log('Result from server:', result);
      
      if (result.success) {
        setStatus('success')
        
        console.log('Actions generated:', result.actions?.length || 0, 'actionsCount:', result.actionsCount);
        
        if (result.actions && result.actions.length > 0 || result.actionsCount > 0) {
          // Navigate to actions dashboard after short delay
          console.log('Navigating to actions dashboard in 1 second...');
          setTimeout(() => {
            const domain = siteKeyFromUrl(url)
            const navigateUrl = `/dashboard/actions?domain=${encodeURIComponent(domain)}&jobId=${jobId}`;
            console.log('Navigating to:', navigateUrl);
            navigate(navigateUrl)
          }, 1000)
        } else {
          // Show success for 3 seconds if no actions generated
          console.log('No actions generated, showing success message');
          setTimeout(() => setStatus(null), 3000)
        }
      } else {
        throw new Error(result.message || 'Failed to generate actions')
      }
      
    } catch (error) {
      console.error('Error generating actions:', error)
      setStatus('error')
      // Show error for 3 seconds
      setTimeout(() => setStatus(null), 3000)
    } finally {
      setIsGenerating(false)
    }
  }

  // Don't disable for jobs without folderPath - we'll try to find it

  if (isChecking) {
    return (
      <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-400">
        <span className="animate-spin mr-1">⏳</span>
        <span>...</span>
      </span>
    )
  }

  return (
    <button
      onClick={handleButtonClick}
      disabled={isGenerating || isChecking}
      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
        status === 'success' 
          ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
          : status === 'error'
          ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
          : hasActions
          ? 'bg-card text-card-foreground border border-border hover:bg-background hover:border-gray-400'
          : 'bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100'
      } disabled:opacity-50 disabled:cursor-not-allowed`}
      title={isChecking ? 'Checking for actions...' : hasActions ? 'View SEO actions' : status === 'success' ? 'Actions generated!' : 'Generate SEO actions from analysis'}
    >
      {isChecking ? (
        <>
          <span className="animate-pulse mr-1">⏳</span>
          <span>Checking...</span>
        </>
      ) : isGenerating ? (
        <>
          <span className="animate-spin mr-1">⏳</span>
          <span>...</span>
        </>
      ) : status === 'success' ? (
        <>
          <span className="mr-1">✅</span>
          <span>Done!</span>
        </>
      ) : status === 'error' ? (
        <>
          <span className="mr-1">❌</span>
          <span>Error</span>
        </>
      ) : hasActions ? (
        <>
          <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          <span>{localActionCount > 0 ? `${localActionCount} Actions` : actionCount > 0 ? `${actionCount} Actions` : 'View Actions'}</span>
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span>Generate Actions</span>
        </>
      )}
    </button>
  )
}



export default JobsHistory